import { useState, useEffect, useCallback } from "react";
import {
  externalCandidateService,
  ExternalCandidate,
  ExternalPagination,
  CandidateFilters,
} from "../services/api/externalCandidate";
import { toast } from "sonner";

export const useExternalCandidates = (
  initialPage = 1,
  initialPageSize = 10,
  initialFilters: CandidateFilters = {},
) => {
  const [candidates, setCandidates] = useState<ExternalCandidate[]>([]);
  const [pagination, setPagination] = useState<ExternalPagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [filters, setFilters] = useState<CandidateFilters>(initialFilters);

  const updateFilters = useCallback((newFilters: CandidateFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page on filter change
  }, []);

  const fetchCandidates = useCallback(
    async (page: number, size: number, currentFilters: CandidateFilters) => {
      setLoading(true);
      setError(null);
      try {
        const response = await externalCandidateService.fetchCandidates(
          page,
          size,
          currentFilters,
        );
        if (response.success) {
          setCandidates(response.data);
          if (response.pagination) {
            setPagination(response.pagination);
          }
        } else {
          // const msg = response.message || "Failed to fetch candidates";
          // setError(msg);
          // toast.error(msg);
        }
      } catch (err: any) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          "An error occurred while fetching candidates";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchCandidates(currentPage, pageSize, filters);
  }, [currentPage, pageSize, filters, fetchCandidates]);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  return {
    candidates,
    pagination,
    loading,
    error,
    currentPage,
    pageSize,
    handlePageChange,
    updateFilters,
    refresh: () => fetchCandidates(currentPage, pageSize, filters),
  };
};
